// Agentic kernel save. Replaces the mechanical extractor — the
// extraction was doing very little real work (list tool calls, last
// render_block wins) and was the source of every robustness failure:
// captured failed retries, couldn't parameterise, couldn't recognise
// when a value was accidental, couldn't audit the recipe against
// schema drift.
//
// Now: a sub-agent reads a structured summary of the recent
// transcript, proposes a kernel JSON, dry-runs it via `test_kernel`,
// fixes errors, and returns a validated kernel via `return_result`.
// The user sees the sub-agent's progress inline (same SubAgentSlot
// surface as spawn_subagent) and can STOP if it loops.

import { mirrorTranscript, refreshMirror } from "./agentTranscript.js";
import { runSubAgent } from "./subAgent.js";
import { createTestKernelTool } from "./kernelTestTool.js";
import { schemaTools } from "./schemaSearch.js";
import { analyticsQueryTool } from "./analyticsTool.js";
import { createSpawnQueryTool } from "./spawnQueryTool.js";
import { saveKernelCloudFirst, sanitizeCommand } from "./kernelStore.js";
import { createInspectSubAgentTool } from "./subAgentTools.js";
import { SERVER_REGISTRY } from "./mcp/index.js";
import { coerceJsonArg } from "./coerceJsonArg.js";
import {
    formatKernelShareWarnings,
    kernelShareWarnings,
} from "./kernelShareSafety.js";

// MCP tool calls are named `<server>__<tool>` by the agentTools adapter
// (e.g. `linear__list_issues`). Recognise the ones whose prefix is a
// registered server so the extractor can lift them into mcp_tool steps —
// the generic fallback would drop their args + result, which a recipe
// can't reconstruct from.
function parseMcpToolName(name) {
    if (typeof name !== "string") return null;
    const idx = name.indexOf("__");
    if (idx <= 0) return null;
    const server = name.slice(0, idx);
    const tool = name.slice(idx + 2);
    if (!tool || !SERVER_REGISTRY[server]) return null;
    return { server, tool };
}

// The tool names whose presence makes a turn "analysis" — i.e. material
// a kernel can actually be built from. Shared by both the window
// boundary logic (which barren trailing turns to skip) and the
// per-turn summary (which lines to flag as analysis).
// Tools that run analytics queries for the model. Their own arguments
// carry no spec; the queries they ran are in their result details.
const HARNESS_QUERY_TOOLS = new Set(["pivot", "expand", "expand_all", "run_recipe"]);

function isAnalysisToolName(name) {
    return name === "analytics_query"
        || HARNESS_QUERY_TOOLS.has(name)
        || name === "execute_python"
        || name === "render_block"
        || !!parseMcpToolName(name);
}

// True when a raw Pi assistant message carries an analysis tool call.
function messageHasAnalysisCall(m) {
    if (m?.role !== "assistant" || !Array.isArray(m.content)) return false;
    return m.content.some((b) => b?.type === "toolCall" && isAnalysisToolName(b.name));
}

// The query specialist, available to the extractor exactly as it is to
// the main chat: hand off a query spec the extractor can't get right
// and get a VALIDATED one back. It pulls its toolkit (analytics_query +
// the three schema tools — pi's SPECIALIST_TOOLKIT) from this fixed
// registry. Stateless, so built once at module load.
const spawnQueryTool = createSpawnQueryTool({
    toolRegistry: () => [analyticsQueryTool, ...schemaTools],
});

// Public entry point. Returns a handle:
//   { promise, abort }
// `promise` resolves to { ok, saved?, error? }.
// `abort` cancels the in-flight sub-agent (UI's STOP button).
// `onState` receives sub-agent slot deltas the UI can mirror.
export function startKernelSave({
    chat,
    command,
    title,
    description,
    feedback,
    priorKernel,
    dashboardContext,
    proposedRender,
    provenanceHint,
    // The canvas item's tagged provenance, when this save is one arm of a
    // canvas fan-out. Scopes the transcript window to the turns that produced
    // THIS panel; see summariseRecentWindow. Omit it (undefined) for a plain
    // /save-kernel, which wants the most recent analysis window.
    sourceToolCallIds,
    agentEnabled,
    runContext = null,
    thinkingLevel,
    onState,
}) {
    const controller = new AbortController();

    const promise = (async () => {
        const cmd = sanitizeCommand(command);
        if (!cmd) {
            return { ok: false, error: "invalid kernel command name" };
        }

        const transcript = summariseRecentWindow(chat, {
            anchorToolCallIds: Array.isArray(sourceToolCallIds)
                ? sourceToolCallIds
                : null,
        });
        const inspectTool = createInspectSubAgentTool({ chat });
        const extraction = await runKernelExtraction({
            transcript,
            command: cmd,
            title,
            description,
            feedback,
            priorKernel,
            dashboardContext,
            proposedRender,
            provenanceHint,
            agentEnabled,
            runContext,
            thinkingLevel,
            inspectTool,
            signal: controller.signal,
            onState,
        });
        if (!extraction.ok) return extraction;

        try {
            const saved = await saveKernelCloudFirst(null, extraction.kernel);
            if (saved?.cloudSaveStatus) {
                return {
                    ok: false,
                    error: saved.cloudSaveMessage || "kernel remains stored locally",
                    kernel: extraction.kernel,
                };
            }
            return {
                ok: true,
                saved,
                warning: formatKernelShareWarnings(
                    kernelShareWarnings(extraction.kernel),
                ),
            };
        } catch (err) {
            return {
                ok: false,
                error: `kernel passed validation but storage rejected it: ${err?.message || err}`,
                kernel: extraction.kernel,
            };
        }
    })();

    return {
        promise,
        abort: () => {
            try { controller.abort(); } catch { /* noop */ }
        },
    };
}

// Core transcript → validated kernel JSON. Shared by the main-chat
// save flow (startKernelSave, which then persists) and the kernel
// editor's commit_to_kernel tool (which loads the result into the
// draft instead of saving). Returns { ok, kernel } | { ok: false,
// error, kernel? } and NEVER touches storage.
//
// `transcript` is the structured window produced by either
// summariseRecentWindow (ChatConsole's UI item tree) or
// summariseRecentMessages (a raw Pi message array) — both emit the
// same { turnCount, hasAnyAnalysis, hasRenderBlock, summary } shape.
// `inspectTool` is optional: the main chat passes one so the
// extractor can introspect sub-agent calls; the editor omits it.
export async function runKernelExtraction({
    transcript,
    command,
    title,
    description,
    feedback,
    priorKernel,
    dashboardContext,
    proposedRender,
    provenanceHint,
    agentEnabled = false,
    runContext = null,
    inspectTool = null,
    // The chat's currently-selected thinking depth, threaded from the
    // UI so the extractor reasons at the depth the user picked. null
    // (e.g. the editor's commit_to_kernel, which has no thinking picker)
    // defers to subAgent's per-model default for primary-role children.
    thinkingLevel = null,
    signal,
    onState,
}) {
    const cmd = sanitizeCommand(command);
    if (!cmd) {
        return { ok: false, error: "invalid kernel command name" };
    }
    if (!transcript || !transcript.turnCount) {
        return { ok: false, error: "no recent turn to extract from — run an analysis first" };
    }
    if (!transcript.hasAnyAnalysis) {
        return {
            ok: false,
            error: "the recent transcript has no analytics_query / execute_python / render_block calls — nothing to crystallise. Run an analysis ending in render_block.",
        };
    }
    // When the caller supplies a proposedRender (Pattern B from
    // suggest_kernel — the agent described a chart in text and the
    // user is choosing it), the lack of a render_block in the
    // transcript is expected. The sub-agent will use proposedRender
    // verbatim instead of lifting one from history.
    if (!transcript.hasRenderBlock && !proposedRender) {
        return {
            ok: false,
            error: "the recent transcript didn't produce a successful render_block — kernels need a render to display their result. Call render_block on the final value first.",
        };
    }

    const systemPrompt = buildSystemPrompt({ agentEnabled });
    const task = buildTask({ command: cmd, title, description, dashboardContext, transcript, feedback, priorKernel, proposedRender, provenanceHint });

    const slotState = makeSlot();
    const emit = () => { try { onState?.({ ...slotState }); } catch { /* noop */ } };
    const onEvent = (event) => {
        forwardChildEvent(slotState, event);
        emit();
    };
    const onChildReady = (child) => {
        mirrorTranscript(slotState, child);
        emit();
    };

    // The extractor's repair toolkit. Before, its ONLY way to validate
    // a query was a whole-kernel test_kernel dry-run, and its only
    // repair aid was static schema lookup — so a single broken/
    // parameterised analytics_query spec meant looping on the full
    // kernel with no way to iterate on the query in isolation. Now it
    // also gets:
    //   • analytics_query — dry-run a SINGLE query spec on its own,
    //     before wiring it into the kernel.
    //   • spawn_query — hand a query it can't get right to the
    //     unbounded specialist and get a validated spec back.
    // Automated repair iterations never own an interactive consent broker:
    // prompting from inside a retry loop would turn approval into theater.
    // The final whole-kernel validation is different—it is the one execution
    // directly attributable to the explicit save action—so only that call
    // receives the caller's ephemeral LIVE context.
    const repairTestKernelTool = createTestKernelTool({ runContext: null });
    const finalTestKernelTool = runContext
        ? createTestKernelTool({ runContext })
        : repairTestKernelTool;
    const tools = [
        repairTestKernelTool,
        analyticsQueryTool,
        spawnQueryTool,
        ...(inspectTool ? [inspectTool] : []),
        ...schemaTools,
    ];
    const { status, result, error, reason, lastAssistantText, messages } = await runSubAgent({
        task,
        systemPrompt,
        tools,
        outputSchema: KERNEL_OUTPUT_SCHEMA,
        signal,
        onEvent,
        onChildReady,
        // Kernel *generation* — composing candidate JSON, dry-running it
        // via test_kernel, and fixing schema errors across retries — is
        // creative generation work, not classification. It runs on the
        // primary model. Only the cheap "task" model handles the
        // suggest/dedupe *classification* (see kernelDedupeJudge.js),
        // which is comparison work. Routing generation to "task" (the
        // state carried over from the Origin promotion) made medium+
        // kernels fail on the small model.
        role: "primary",
        // Explicit selected depth from the chat. A truthy level wins in
        // subAgent; null lets its per-model fallback supply the default
        // (so this composes with — rather than replaces — that safety net).
        thinkingLevel,
    });

    if (status === "aborted") {
        return { ok: false, error: "extraction cancelled" };
    }
    // The extractor's LLM request was rejected outright (the proxy 400'd —
    // e.g. an invalid thinking config) so the model never ran. subAgent
    // surfaces the swallowed error here instead of letting it masquerade as
    // the transient no_return_result below; a 400 is deterministic, so the
    // copy must NOT tell the user to just try again.
    if (reason === "llm_error") {
        console.warn("[kernel-save] extractor LLM request failed", {
            error: error?.message,
            turns: Array.isArray(messages) ? messages.length : 0,
        });
        return {
            ok: false,
            error:
                "Couldn't build the kernel — the analysis model rejected the request: " +
                `${error?.message || "unknown error"}. This isn't transient; retrying won't help.`,
        };
    }
    // The extractor ran to completion but never emitted a kernel (even
    // after subAgent's return_result nudge). The raw internal string used
    // to leak straight to the user and the transcript was discarded; log
    // the preserved transcript for diagnosis and surface actionable copy.
    if (reason === "no_return_result") {
        console.warn("[kernel-save] extractor finished without returning a kernel", {
            turns: Array.isArray(messages) ? messages.length : 0,
            lastAssistantText,
        });
        return {
            ok: false,
            // Action-neutral wording: this message surfaces both in the
            // main-chat kernel card AND the kernel editor's commit_to_kernel
            // tool result, where there is no "Save" action to reference.
            error:
                "Couldn't finalize the kernel — the extractor stopped without returning a recipe. " +
                "This is usually transient; try again. If it keeps happening, the analysis may " +
                "be hard to crystallize automatically (e.g. a query that no longer validates).",
        };
    }
    if (status !== "done" || !result || typeof result !== "object") {
        return {
            ok: false,
            error: error?.message || "extraction agent did not return a kernel",
        };
    }

    // Defence-in-depth: even though the sub-agent should only call
    // return_result after test_kernel passes, run one more dry-run
    // from this side so we never hand back a broken JSON. Caller
    // overrides (command, title, description) win over the extractor's
    // generated values.
    // `agentic` is true ONLY when this was an agent-enabled extraction AND
    // the result actually uses an agent step. A deterministic extraction
    // can never produce an agentic kernel — so any agent step that leaked
    // into a non-agent-enabled run lands without the flag and gets rejected
    // (by the runtime gate during the dry-run below, then by saveKernel).
    const hasAgentStep = Array.isArray(result?.steps) && result.steps.some((s) => s && s.kind === "agent");
    const finalKernel = {
        ...result,
        command: cmd,
        title: title || result.title || cmd,
        ...(typeof description === "string" && description.trim()
            ? { description: description.trim() }
            : {}),
        agentic: agentEnabled && hasAgentStep ? true : undefined,
    };
    const finalTest = await finalTestKernelTool.execute(
        "final-validate",
        { kernel: finalKernel },
    );
    const finalDetails = finalTest?.details || {};
    if (finalDetails.ok === false) {
        return {
            ok: false,
            error: `agent returned a kernel that still fails: ${finalDetails.error}`,
            kernel: finalKernel,
        };
    }

    return { ok: true, kernel: finalKernel };
}

// ── system prompt ─────────────────────────────────────────────────

function buildSystemPrompt({ agentEnabled = false } = {}) {
    return [
        "You are extracting a saved kernel from a recent in-chat analysis.",
        "",
        agentEnabled
            ? "A KERNEL re-executes outside the chat. It is normally fully DETERMINISTIC (analytics_query + python + mcp_tool, no LLM). THIS extraction is AGENT-ENABLED: you MAY add `agent` steps (a tool-less in-line LLM returning strict structured output) — but ONLY where a transform needs semantic judgment a python expression can't deterministically encode. Prefer deterministic steps; reach for an agent step sparingly and never where python would do."
            : "A KERNEL is a deterministic recipe that can re-execute outside the chat. It captures the SUCCESSFUL analytics_query / execute_python / render_block calls from the recent transcript and binds them together. The kernel runner has no LLM — it just runs the steps in order, so the JSON you return must be fully self-contained.",
        "",
        "## Kernel JSON shape",
        "",
        "```",
        "{",
        '  "command": "<slash-command, kebab-case>",',
        '  "title": "<human label>",',
        '  "description": "<optional>",',
        '  "scopeMode": "scoped" | "global",',
        ...(agentEnabled ? ['  "agentic": true,   // REQUIRED whenever you include an agent step'] : []),
        '  "params": { "<name>": { "type": "number"|"string"|"boolean", "default": <value> } },',
        '  "steps": [',
        '    { "kind": "analytics_query", "id": "q1", "spec": { <analytics Query spec> } },',
        '    { "kind": "python",          "id": "py1", "code": "<python source>", "inputs": { "<name>": "<prior-step-id>" }, "packages": ["numpy"|"pandas"|"scipy"] },',
        '    { "kind": "mcp_tool",         "id": "m1", "server": "<server-id>", "tool": "<tool-name>", "args": { <static args> }, "argsFrom": "<prior-step-id, optional>" }' + (agentEnabled ? "," : ""),
        ...(agentEnabled ? ['    { "kind": "agent",            "id": "a1", "inputs": { "<name>": "<prior-step-id>" }, "prompt": "<what to produce>", "outputSchema": { <JSON Schema the result MUST match> } }'] : []),
        "  ],",
        '  "render": { <render_block spec> },',
        '  "renderSource": "<id of the step whose output feeds the render>"',
        "}",
        "```",
        "",
        "## Rules",
        "",
        "1. Pick the SUCCESSFUL tool calls from the transcript. Ignore failed retries — the model may have hit an error and tried again with corrected code; only the working version belongs in the kernel.",
        "2. Order matters: steps run sequentially. analytics_query steps are independent; python steps can reference prior outputs via their `inputs` field. NEVER reference variables that come from outside the kernel (chat-side globals, prior unrelated cells) — wire dependencies through `inputs`.",
        "3. Python globals at runtime: the runner sets TWO globals before executing each python step — `inputs` and `params`, both real Python dicts. Access them as `inputs[\"q1\"]` (returns q1's rows as a list of row-dicts, suitable for `pd.DataFrame(inputs[\"q1\"])`) and `params[\"days\"]` (returns the resolved parameter value). Do NOT wrap them in a `data` envelope — there is no `data` global in kernel runs. The runner does NOT preserve any state from the original chat session, so anything the python step needs must come from `inputs` or `params`.",
        "",
        "4. Python step output: the step's output is whichever of these is set —",
        "   Python dependencies are declarative: add `packages` with only the roots the code imports (`numpy`, `pandas`, and/or `scipy`). Omit it when none are used. Packages load lazily; never use pip or dynamic installation.",
        "   (a) the last EXPRESSION in your code (i.e. ending the code with `df.to_dict(\"records\")` returns that value), OR",
        "   (b) a variable named `result` (Jupyter idiom — `result = anomalies.to_dict(\"records\")` works).",
        "   Either is fine; pick whichever reads better. If both are set, `result` wins. The runner clears `result` before each step so values don't leak between steps.",
        "4. Parameterise sensible literal values into `params` with defaults that match the analysis's apparent intent (limits like `days=7`, thresholds like `z=2`). Skip values that are clearly structural (entity names, field enums, the spec's overall shape).",
        "5. Render: copy the SUCCESSFUL render_block spec verbatim. If the spec inlines a `series: [N,N,N]` array, REWRITE IT to use `valueField` (line mode) or `layerFields` (stacked) so the render projects from row data at runtime instead of replaying stale numbers. The render's `data` will come from the renderSource step's output automatically.",
        "6. Time ranges: if the original analytics_query used a relative time range (`timeRange: {relative: {durationSeconds: N}}`), KEEP IT relative — kernels reaping fresh data is the whole point. If it used absolute timestamps, consider parameterising the duration so users can vary the window.",
        "6b. SCOPE — set `scopeMode` and, if scoped, STRIP the authoring scope. The activity surface re-runs a kernel under whatever scope the user is viewing (an endpoint, a cluster, the global overview). Decide which kind THIS kernel is:",
        "   • SCOPE-DEPENDENT → `scopeMode: \"scoped\"`. Use this when the analysis is a function of a set of PROMPTS and its analytics_query roots at `ENTITY_TYPE_PROMPT` — e.g. token volume, prompt count over time, top tools. CRITICAL: author the query WITHOUT the current dashboard scope baked in. The transcript's query was run under the scope shown in `Current dashboard scope:` above, so its spec almost certainly carries a predicate that just narrows to that scope (e.g. `DIMENSION_FIELD_PROMPT_ENDPOINT_ID = <the endpoint you were on>`, or a cluster/session/agent/model filter matching it). REMOVE those scope predicates. KEEP only intrinsic filters the analysis genuinely needs (e.g. `token_count > 1000`, a status enum). The runner re-injects the LIVE scope (root + filter) at run time — if you leave the authoring scope in, the kernel double-filters and freezes to where it was built.",
        "   • SCOPE-AGNOSTIC → `scopeMode: \"global\"`. Use this when the analysis is global by nature, or its query roots at a NON-prompt entity (e.g. `ENTITY_TYPE_ENDPOINT`, `ENTITY_TYPE_AI_SESSION`, `ENTITY_TYPE_ILO_EVENT_CLUSTER` as the root). The runner runs it unchanged everywhere; it can only be mounted on the overview.",
        "   • When unsure: prefer `scoped` for a prompt-rooted prompt-set metric, `global` otherwise. If you strip a scope predicate, re-validate the query with `analytics_query` so you know it still runs unscoped.",
        "6c. MCP TOOLS — the transcript may include MCP tool calls (named `<server>__<tool>`, e.g. `linear__list_issues`). Crystallise them as `{ \"kind\": \"mcp_tool\", \"id\", \"server\", \"tool\", \"args\"?, \"argsFrom\"? }` steps. Two hard rules:",
        "   • READ-ONLY ONLY. Kernels are analysis recipes that re-run freely (on every scope change, refresh, remount). Only lift tools that READ/LIST/SEARCH/GET. NEVER lift a tool that creates, updates, deletes, posts, comments, or otherwise writes — drop it from the recipe even if it was in the transcript.",
        "   • ARG-WIRING (the important part). In the chat, the tool's `args` were concrete values the model computed in its head — frequently DERIVED from a prior analytics_query / execute_python result. For the kernel to re-run live, those args must be DERIVED, not frozen. So: if an arg value came from (or depends on) a prior step's output, DO NOT hard-code it. Instead insert a `python` step that reads that prior step via `inputs`, reshapes it into the exact arguments object the tool wants (`result = { \"query\": inputs[\"q1\"][0][\"topic\"], \"limit\": 5 }`), and set the mcp_tool's `argsFrom` to that python step's id. The runner uses that step's output as the call arguments. Keep values in inline `args` ONLY when they are genuinely constant or a kernel `param`. When in doubt whether an arg was derived, wire it through python — a frozen id/string that was really a lookup result is the #1 way these kernels silently rot.",
        "   • The kernel is only offered when the server is connected; you don't need to handle the disconnected case.",
        ...(agentEnabled ? [
            "6d. AGENT STEPS (permitted only because this is an AGENT-ENABLED extraction). `{ \"kind\": \"agent\", \"id\", \"inputs\": { <name>: <prior-step-id> }, \"prompt\", \"outputSchema\" }` — a tool-less in-line LLM that reshapes its inputs into a result that MUST match `outputSchema` (strict structured output). Its result lands in the outputs map, so it can feed a python `inputs`, an mcp_tool `argsFrom`, the render `renderSource`, or another agent.",
            "   • PREFER PYTHON. Use an agent step ONLY when the transform needs genuine semantic judgment a python expression can't encode — e.g. 'pick the most security-relevant topic from these rows', 'turn this fuzzy label into a good search query'. If python can compute it deterministically, use python — it's reproducible and free; an agent step runs an LLM on every execution.",
            "   • TOOL-LESS. An agent step has no tools and must not fetch anything — it only reasons over its inputs. The real tool call stays in the deterministic mcp_tool step downstream.",
            "   • When an agent step feeds an mcp_tool via `argsFrom`, its `outputSchema` MUST be exactly that tool's input schema (no extra fields) — the tool rejects unknown args. Emit only the arguments object the tool accepts.",
            "   • Set the kernel's `agentic: true` whenever you include an agent step.",
        ] : []),
        "7. Sub-agent calls in the transcript: a `spawn_subagent` / `spawn_batch` entry hides its child's internal tool calls. If the kernel recipe depends on data the child produced (e.g. the parent piped the child's result into execute_python via `from_tool_calls`), call `inspect_subagent({tool_call_id: \"<id from [id=…] tag>\"})` to see the child's analytics_query / execute_python / render_block calls. Lift the SUCCESSFUL ones into the kernel's `steps` as if they had happened in the main chat — the kernel runner has no concept of sub-agents, so the recipe must be flattened. If the spawn_* call was incidental (e.g. used for narrative reasoning, not for producing the rendered data), you can ignore its internals.",
        "",
        "## Workflow",
        "",
        "1. Read the recent transcript I'm about to give you. Identify the successful chain: which analytics_query/queries → which python step → which render_block.",
        "2. Compose a candidate kernel JSON in your head.",
        "3. Validate the query specs FIRST, in isolation, before paying for a whole-kernel run. When you've modified a query from the transcript — parameterised a literal, generalised a filter, changed a relative time range — run that single spec via `analytics_query` to confirm it still executes and returns the shape you expect. A spec that fails here is far cheaper to find than one that only blows up inside `test_kernel` three steps deep.",
        "4. Call `test_kernel(kernel)` to dry-run the whole recipe end-to-end.",
        "5. If `ok: false`, read the `error` and `stage` fields, fix the offending step, retry.",
        "   - `analytics_query` failure → first verify field/entity names with `schema_search` / `schema_describe_entity`, then re-validate the single spec with `analytics_query`. If you've fixed it twice and it still won't run — or you genuinely don't know which field/edge/operator the query needs — hand it to `spawn_query` (describe the query, pass your failing `spec` as `failed_attempt` and the error as `failed_error`); it iterates against the schema with no retry cap and returns a VALIDATED spec you drop straight into the step. Don't loop on the same broken spec yourself.",
        "   - `python` failure → fix the code (common: forgot to wire an input through `inputs`, or used `data.foo` instead of `data[\"foo\"]`).",
        "   - `render` failure → adjust the render spec so its expected data shape matches what the renderSource step produces.",
        "6. When `test_kernel` returns `ok: true`, call `return_result` with the validated kernel JSON.",
        "",
        "`analytics_query` and `spawn_query` are validation aids only — your deliverable is still the kernel JSON. Don't return query rows; return the recipe. Do not call `return_result` until `test_kernel` confirms `ok: true`. Do not narrate the kernel in assistant text — the tool call IS the answer.",
    ].join("\n");
}

const KERNEL_OUTPUT_SCHEMA = {
    type: "object",
    description: "A validated kernel definition with command, title, params, steps, render, renderSource.",
    required: ["command", "title", "steps", "render", "scopeMode"],
    properties: {
        command: { type: "string" },
        title: { type: "string" },
        description: { type: "string" },
        scopeMode: {
            type: "string",
            enum: ["scoped", "global"],
            description: "\"scoped\": the activity surface re-runs this kernel narrowed to the active scope (prompt-rooted queries, authored WITHOUT the current scope baked in). \"global\": runs tenant-wide everywhere.",
        },
        agentic: {
            type: "boolean",
            description: "Set true ONLY when the kernel includes an `agent` step (agent-enabled extractions only). Omit for deterministic kernels.",
        },
        params: { type: "object" },
        steps: { type: "array" },
        render: { type: "object" },
        renderSource: { type: "string" },
    },
};

// ── transcript window ────────────────────────────────────────────

function buildTask({ command, title, description, dashboardContext, transcript, feedback, priorKernel, proposedRender, provenanceHint }) {
    const ctxLines = [];
    if (dashboardContext?.range) ctxLines.push(`Current dashboard range: ${dashboardContext.range}`);
    if (dashboardContext?.scope?.length) {
        const scopeText = dashboardContext.scope
            .map((s) => `${s.dimLabel || s.dim}=${s.label || s.id}`)
            .join(" / ");
        ctxLines.push(`Current dashboard scope: ${scopeText}`);
    }

    const lines = [
        `Target kernel command: /${command}`,
        title ? `Target kernel title: ${title}` : "Target kernel title: (use what fits the analysis)",
        ...(typeof description === "string" && description.trim()
            ? [`Target kernel description: ${description.trim()}`]
            : []),
        ...ctxLines,
        "",
        "## Recent transcript",
        "",
        transcript.summary,
        "",
        "Build a kernel JSON that captures the successful recipe shown above, dry-run it via test_kernel, fix errors, return_result when it passes.",
    ];
    // Pattern B (suggest_kernel with proposedRender): the user picked
    // a render the agent had described but never actually rendered.
    // The transcript has the data steps but no render_block; the
    // proposed spec below is the one to use. We still expect the
    // sub-agent to wire renderSource correctly and rewrite inline
    // series→valueField the same way as the lift-from-transcript path.
    if (proposedRender && typeof proposedRender === "object") {
        lines.push(
            "",
            "## Proposed render spec (REQUIRED)",
            "",
            "The user is saving a chart that was described in text but not rendered via render_block in the transcript. Use the following render spec verbatim as the kernel's `render` field — do NOT try to lift one from the transcript:",
            "",
            "```json",
            JSON.stringify(proposedRender, null, 2),
            "```",
            "",
            "Your job for the render: keep this spec's `kind` and field references as-is. If the spec inlines a `series: [N,N,N]` array, rewrite it to use `valueField` / `layerFields` so the kernel projects from row data at run time. Then wire `renderSource` to the step whose output matches the spec's expected data shape. The data path (analytics_query → optional python) you still derive from the transcript as usual.",
        );
    }
    // Provenance hint — supplied by the canvas-to-kernels save flow
    // when the agent tagged a canvas item with `sourceToolCallIds` at
    // pin time. Surfaced as a clearly delimited block so the
    // sub-agent uses it as a starting point but doesn't blindly lift
    // (the agent's tag may include failed retries, or the recipe may
    // need transformations like parameterising literals or rewriting
    // inline render-spec series to valueField refs).
    if (typeof provenanceHint === "string" && provenanceHint.trim()) {
        lines.push(
            "",
            "## Provenance hint",
            "",
            provenanceHint.trim(),
        );
    }
    // Rebuild context: the prior kernel JSON that the feedback is
    // about. Without it the agent re-derives from the transcript blind
    // to what it produced last time, so feedback like "the previous
    // build double-counted retries" has no referent and the same
    // mistake recurs. Emit it right before the feedback block so
    // "address the feedback" points at a concrete artifact. Only when
    // it actually carries a recipe — an empty/skeleton draft is noise.
    if (priorKernel && typeof priorKernel === "object" && Array.isArray(priorKernel.steps) && priorKernel.steps.length) {
        let priorJson;
        try { priorJson = JSON.stringify(priorKernel, null, 2); }
        catch { priorJson = null; }
        if (priorJson) {
            lines.push(
                "",
                "## Previous kernel (the one being rebuilt)",
                "",
                "This is the kernel the user saved last time and was unhappy with. The feedback below is about THIS JSON. Diff your new build against it — keep what worked, change what the feedback calls out. Do NOT just reproduce it.",
                "",
                "```json",
                priorJson,
                "```",
            );
        }
    }
    // Optional rebuild feedback: the user previously saved this
    // kernel, ran it, and didn't like the result. They've now asked
    // for a rebuild with a specific complaint. Pass it through so the
    // extractor knows what to address. Surfaced as a separate clearly
    // delimited block so the agent doesn't conflate it with the
    // transcript itself.
    if (typeof feedback === "string" && feedback.trim()) {
        lines.push(
            "",
            "## Rebuild feedback",
            "",
            "The user already saved a version of this kernel from the same transcript and was unhappy with the result. They asked you to rebuild with the following feedback in mind:",
            "",
            feedback.trim(),
            "",
            "Address the feedback in the new kernel: change the analytics_query spec, the python code, the params, or the render spec as needed. Run test_kernel as usual and return_result with the corrected JSON.",
        );
    }
    return lines.join("\n");
}

// True when a UI turn item carries at least one analysis tool call.
function turnHasAnalysis(turn) {
    return (turn?.children || []).some(
        (c) => c.kind === "tool" && isAnalysisToolName(c.name),
    );
}

// Collect the turns of the most-recent chat window that actually
// contains analysis. The naïve "walk back to the last user message"
// breaks the natural-language save path: when the user types "save a
// kernel for me", THAT message becomes the boundary and the only turn
// after it is the agent's barren suggest_kernel call — zero analysis,
// so the extractor finds nothing. Instead we only let a user message
// anchor the window once the turns collected since it actually contain
// analysis; a barren trailing segment (a save request, chit-chat) is
// discarded and we keep walking back to the real analysis turn.
function collectAnalysisWindow(items) {
    let acc = [];
    let accHasAnalysis = false;
    for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];
        if (item?.kind === "turn") {
            acc.unshift(item);
            if (turnHasAnalysis(item)) accHasAnalysis = true;
            continue;
        }
        if (item?.kind === "user") {
            if (accHasAnalysis) break;   // this message started the analysis window
            acc = [];                    // barren segment — drop it, keep looking back
            accHasAnalysis = false;
        }
        // kernel / canvas / system items don't bound the window — skip.
    }
    return acc;
}

// Cap on the widened window (see collectWidenedAnalysisWindow). Every turn is
// rendered with its python code and query specs in full, so an unbounded walk
// back through a long chat would blow the extractor's context on transcript
// instead of leaving room for it to reason.
const MAX_WIDENED_ANALYSIS_TURNS = 12;

// The turns that produced a SPECIFIC canvas item, located by the tool-call ids
// the agent tagged it with at pin time.
//
// A canvas is assembled across many user turns: panel 1 comes from the first
// question, panel 7 from the seventh. collectAnalysisWindow only ever returns
// the LAST analysis window, so handing it to all N kernel sub-agents leaves
// every panel but the most recent one with a transcript that does not contain
// its own producing chain. The provenance hint then tells the sub-agent to find
// ids that aren't there, and it either fails or invents a recipe that looked
// right in preview and is wrong once saved. Anchor on the item's own ids
// instead: take the window from the user message that opened the earliest
// matching turn through the latest matching turn.
//
// Returns null when none of the ids appear in this transcript, so the caller
// can fall back rather than send an empty window.
function collectTaggedAnalysisWindow(items, toolCallIds) {
    const wanted = new Set(toolCallIds);
    const matches = [];
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item?.kind !== "turn") continue;
        const hit = (item.children || []).some(
            (c) => c?.kind === "tool" && c.id && wanted.has(c.id),
        );
        if (hit) matches.push(i);
    }
    if (!matches.length) return null;
    const first = matches[0];
    const last = matches[matches.length - 1];
    // Walk back to the user message that opened the earliest matching turn, the
    // same boundary collectAnalysisWindow uses, so the sub-agent sees the whole
    // chain and not just the tagged calls.
    let start = 0;
    for (let i = first - 1; i >= 0; i--) {
        if (items[i]?.kind === "user") { start = i + 1; break; }
    }
    return items
        .slice(start, last + 1)
        .filter((item) => item?.kind === "turn");
}

// Every analysis-bearing turn in the chat, most recent MAX_WIDENED_ANALYSIS_TURNS
// kept. Used for a canvas item the agent pinned WITHOUT provenance ids: there is
// nothing to anchor on, and the last-window default is provably the wrong answer
// for any panel that isn't the newest, so widen and let the sub-agent's
// heuristic scan (see buildProvenanceHint's untagged branch) actually have the
// producing chain in front of it.
function collectWidenedAnalysisWindow(items) {
    const turns = items.filter((item) => item?.kind === "turn" && turnHasAnalysis(item));
    return turns.slice(-MAX_WIDENED_ANALYSIS_TURNS);
}

// Summarise the recent chat window as a structured text block the
// sub-agent can read directly. Anchors on the most recent window that
// contains analysis (see collectAnalysisWindow) and renders each
// supported tool call with its args + status + a short result summary.
export function summariseRecentWindow(chat, { anchorToolCallIds = null } = {}) {
    if (!chat || !Array.isArray(chat.items)) {
        return { turnCount: 0, hasAnyAnalysis: false, hasRenderBlock: false, summary: "" };
    }
    // `anchorToolCallIds` distinguishes the two callers. null: a plain
    // /save-kernel, where "the analysis I just ran" is the right window.
    // An array (possibly empty): one item of a canvas fan-out, where the
    // window has to be that item's own producing turns, not whichever turn
    // happens to be last.
    const turns = anchorToolCallIds == null
        ? collectAnalysisWindow(chat.items)
        : (
            (anchorToolCallIds.length
                ? collectTaggedAnalysisWindow(chat.items, anchorToolCallIds)
                : null)
            || collectWidenedAnalysisWindow(chat.items)
        );

    const lines = [];
    let hasAnyAnalysis = false;
    let hasRenderBlock = false;
    for (let t = 0; t < turns.length; t++) {
        const turn = turns[t];
        const children = (turn.children || []).filter((c) => c.kind === "tool");
        if (!children.length) continue;
        lines.push(`### Turn ${t + 1}`);
        for (const tool of children) {
            const block = summariseTool(tool);
            if (!block) continue;
            if (isAnalysisToolName(tool.name)) {
                hasAnyAnalysis = true;
            }
            // Successful render_block is the load-bearing input to the
            // extractor — without one in the transcript the resulting
            // kernel has no render spec to crystallise. Track it so
            // startKernelSave can preflight before paying for a
            // sub-agent run that would only produce junk.
            if (tool.name === "render_block" && tool.status === "done") {
                hasRenderBlock = true;
            }
            lines.push(block);
        }
        lines.push("");
    }
    return {
        turnCount: turns.length,
        hasAnyAnalysis,
        hasRenderBlock,
        summary: lines.join("\n").trim() || "(no tool calls in recent turns)",
    };
}

// Summarise the recent window from a raw Pi message array
// (agent.state.messages) instead of ChatConsole's UI item tree. Used
// by the kernel editor's commit_to_kernel tool, which doesn't keep a
// UI tree. Walks back to the last user message, pairs each assistant
// toolCall block with its toolResult, and reuses summariseTool so the
// output format matches summariseRecentWindow exactly.
export function summariseRecentMessages(messages) {
    if (!Array.isArray(messages) || !messages.length) {
        return { turnCount: 0, hasAnyAnalysis: false, hasRenderBlock: false, summary: "" };
    }
    // Anchor the window at the user message that OWNS the most recent
    // analysis, not merely the last user message. Same defect as the UI
    // path (see collectAnalysisWindow): if the user's last message was a
    // bare "save a kernel for me", the only thing after it is a barren
    // suggest call, so walking back to it finds nothing. Find the last
    // analytics_query / execute_python / render_block / mcp tool call,
    // then start at the user message at or before it.
    let lastAnalysisIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messageHasAnalysisCall(messages[i])) { lastAnalysisIdx = i; break; }
    }
    const anchorFrom = lastAnalysisIdx >= 0 ? lastAnalysisIdx : messages.length - 1;
    let start = 0;
    for (let i = anchorFrom; i >= 0; i--) {
        if (messages[i]?.role === "user") { start = i; break; }
    }
    const window = messages.slice(start);

    // Index toolResults by toolCallId so each assistant toolCall can
    // be paired with its outcome.
    const resultById = new Map();
    for (const m of window) {
        if (m?.role === "toolResult" && m.toolCallId) resultById.set(m.toolCallId, m);
    }

    // Build UI-tree-shaped "tool nodes" grouped per assistant message
    // (one assistant message = one turn), then hand each to the shared
    // summariseTool.
    const turns = [];
    for (const m of window) {
        if (m?.role !== "assistant" || !Array.isArray(m.content)) continue;
        const toolNodes = [];
        for (const block of m.content) {
            if (block?.type !== "toolCall") continue;
            const res = resultById.get(block.id);
            const text = res ? textOf(res.content) : "";
            toolNodes.push({
                kind: "tool",
                id: block.id,
                name: block.name,
                // pi-ai stores args under `arguments`; staged blocks use
                // `input`. Mirror the rest of the codebase's fallback.
                args: block.arguments ?? block.input,
                status: res ? (res.isError ? "error" : "done") : "pending",
                details: res?.details,
                summary: text ? truncate(text, 240) : "",
            });
        }
        if (toolNodes.length) turns.push(toolNodes);
    }

    const lines = [];
    let hasAnyAnalysis = false;
    let hasRenderBlock = false;
    for (let t = 0; t < turns.length; t++) {
        const toolNodes = turns[t];
        lines.push(`### Turn ${t + 1}`);
        for (const tool of toolNodes) {
            const block = summariseTool(tool);
            if (!block) continue;
            if (isAnalysisToolName(tool.name)) {
                hasAnyAnalysis = true;
            }
            if (tool.name === "render_block" && tool.status === "done") {
                hasRenderBlock = true;
            }
            lines.push(block);
        }
        lines.push("");
    }
    return {
        turnCount: turns.length,
        hasAnyAnalysis,
        hasRenderBlock,
        summary: lines.join("\n").trim() || "(no tool calls in recent turns)",
    };
}

function textOf(content) {
    if (typeof content === "string") return content;
    if (!Array.isArray(content)) return "";
    return content.filter((c) => c?.type === "text").map((c) => c.text).join("\n");
}

// Some chat models double-encode object/array tool args as JSON strings
// (`spec: "{\"kind\":...}"` rather than an object) — the same asymmetry
// the analytics_query coerceSpec note and the render_block/canvas
// coerceJsonArg boundary defend against. The transcript records the RAW
// args the model emitted, NOT the runtime-coerced value, so a stringified
// render/query spec would reach the extraction sub-agent here as an
// escaped string and get copied into the kernel verbatim — a broken
// render or query step. Decode the whole args blob and its `spec`/`data`
// sub-fields so every summary branch reads real JSON regardless of which
// model produced the transcript.
function normalizeToolArgs(rawArgs) {
    const args = coerceJsonArg(rawArgs);
    if (!args || typeof args !== "object" || Array.isArray(args)) return args;
    let next = args;
    if (typeof args.spec === "string") next = { ...next, spec: coerceJsonArg(args.spec) };
    if (typeof args.data === "string") next = { ...next, data: coerceJsonArg(args.data) };
    return next;
}

function summariseTool(tool) {
    if (!tool) return null;
    const name = tool.name;
    const status = tool.status;
    const args = normalizeToolArgs(tool.args);
    // Include the tool-call id on every entry so canvas-save's
    // `sourceToolCallIds` hints can be matched to specific transcript
    // lines (analytics_query / execute_python). Same id is what
    // execute_python uses for `from_tool_calls` refs, what
    // canvas_add's `sourceToolCallIds` echo, and what
    // inspect_subagent takes — keep them all aligned.
    const idTag = tool.id ? ` [id=${tool.id}]` : "";
    if (name === "analytics_query") {
        const result = tool.details;
        const rows = Array.isArray(result?.rows) ? result.rows.length : null;
        const cols = Array.isArray(result?.columns) ? result.columns.join(", ") : null;
        const resultLine = status === "done" && rows != null
            ? `→ ${rows} row${rows === 1 ? "" : "s"}${cols ? ` · cols=[${cols}]` : ""}`
            : status === "error"
                ? `→ ERROR · ${tool.summary || "(no message)"}`
                : `→ ${status}`;
        return [`- analytics_query (${status})${idTag}`, indent(formatSpec(args), "    spec: "), `    ${resultLine}`].join("\n");
    }
    if (name === "pivot") {
        const result = tool.details;
        const spec = result?.querySpec || result?.resolvedSpec || null;
        const rows = Array.isArray(result?.rows) ? result.rows.length : null;
        const cols = Array.isArray(result?.columns) ? result.columns.join(", ") : null;
        const resultLine = status === "done" && rows != null
            ? `→ ${rows} row${rows === 1 ? "" : "s"}${cols ? ` · cols=[${cols}]` : ""}`
            : status === "error"
                ? `→ ERROR · ${tool.summary || "(no message)"}`
                : `→ ${status}`;
        return [
            `- pivot (${status})${idTag}  // the harness ran this analytics_query for the model; lift it like one, under this id`,
            indent(spec ? formatSpec({ spec }) : "(spec not recorded)", "    spec: "),
            `    ${resultLine}`,
        ].join("\n");
    }
    if (name === "expand" || name === "expand_all" || name === "run_recipe") {
        const head = `- ${name} (${status})${idTag}`;
        const inner = harnessQuerySteps(tool.details);
        if (!inner.length) return `${head}  // ran queries for the model; none recorded a spec`;
        return [
            `${head}  // the harness ran these analytics_query calls for the model; lift the ones the answer used, each under its own id`,
            ...inner.map((s) => [
                `  - analytics_query via ${name} (${s.error ? "error" : "done"}) [id=${s.toolCallId}]`,
                indent(formatSpec({ spec: s.spec }), "      spec: "),
                `      → ${s.error ? `ERROR · ${truncate(s.error, 160)}` : `${s.rows ?? 0} row${s.rows === 1 ? "" : "s"}`}`,
            ].join("\n")),
        ].join("\n");
    }
    if (name === "execute_python") {
        const details = tool.details;
        const innerError = details?.error;
        const resultShape = details?.result == null
            ? "null"
            : Array.isArray(details.result)
                ? `array · ${details.result.length} items`
                : typeof details.result === "object"
                    ? `object · keys=[${Object.keys(details.result).slice(0, 6).join(", ")}]`
                    : typeof details.result;
        const statusLine = innerError
            ? `→ ERROR · ${truncate(innerError, 240)}`
            : status === "error"
                ? `→ ERROR · ${tool.summary || "(no message)"}`
                : status === "done"
                    ? `→ ${resultShape}`
                    : `→ ${status}`;
        const code = typeof args === "object" ? args?.code : null;
        // The python step's `code` is the kernel deliverable — emit it in
        // full. Clipping it (the old 30-line cap) is the same defect as
        // spec truncation: a step over the cap can't be reproduced.
        const codePreview = typeof code === "string"
            ? "    code:\n" + code.split("\n").map((l) => "      " + l).join("\n")
            : indent(formatArgs(args), "    args: ");
        return [`- execute_python (${innerError ? "error (inner)" : status})${idTag}`, codePreview, `    ${statusLine}`].join("\n");
    }
    if (name === "render_block") {
        const blockArgs = args && typeof args === "object" ? args : null;
        const specText = blockArgs?.spec ? "    spec: " + safeJson(blockArgs.spec) : "";
        const dataNote = blockArgs?.data != null
            ? `    data: ${describeBriefData(blockArgs.data)}`
            : "";
        return [`- render_block (${status})${idTag}`, specText, dataNote].filter(Boolean).join("\n");
    }
    if (name === "schema_search" || name === "schema_describe_entity" || name === "schema_list_entities") {
        return `- ${name} (${status})  // schema lookup — not part of the kernel recipe`;
    }
    const mcp = parseMcpToolName(name);
    if (mcp) {
        const argsText = args != null ? safeJson(args) : "(no args)";
        const resultText = tool.summary ? truncate(tool.summary, 240) : (status === "done" ? "(ok)" : status);
        return [
            `- mcp_tool ${mcp.server}__${mcp.tool} (${status})${idTag}`,
            `    args: ${argsText}`,
            `    → ${resultText}`,
            "    // MCP tool. Read-only only. If any arg was derived from a prior step's result, wire it through a python step + `argsFrom` rather than freezing the literal value.",
        ].join("\n");
    }
    if (name === "spawn_subagent" || name === "spawn_batch") {
        const summaryLine = tool.summary ? ` · ${truncate(tool.summary, 120)}` : "";
        return `- ${name} (${status}) [id=${tool.id}]${summaryLine}  // sub-agent invocation. If this call produced load-bearing data, call inspect_subagent({tool_call_id: "${tool.id}"}) to see the child's internal analytics_query / execute_python / render_block calls — lift them into the kernel's steps.`;
    }
    return `- ${name} (${status})`;
}

// Generic arg fallback (execute_python with no `code`, etc.). Lossless —
// these summaries are reproduction inputs, not context-budget-managed.
function formatArgs(args) {
    if (args == null) return "(no args)";
    if (typeof args === "string") return args;
    try {
        return JSON.stringify(args, null, 2);
    } catch {
        return String(args);
    }
}

// analytics_query / render_block specs ARE the kernel deliverable — the
// extractor copies them verbatim into steps, so this MUST be lossless.
// Serialise compactly (no indent collapses the many empty stepFilter /
// subOrder / children arrays a graph traversal carries — lossless, just
// whitespace), but NEVER truncate. Context-budget management is
// compaction's job (piChat); clipping here is exactly what hid the
// aggregates / orderBy / limit and sent the extractor re-discovering.
function formatSpec(value) {
    if (value == null) return "(no args)";
    return typeof value === "string" ? value : safeJson(value);
}

// The queries an expand, expand_all or run_recipe call ran, from its
// details: each step that kept its spec, including the expands a recipe
// or expand_all ran inside it.
function harnessQuerySteps(details, depth = 0) {
    if (!details || typeof details !== "object" || depth > 4) return [];
    const found = [];
    for (const step of Array.isArray(details.steps) ? details.steps : []) {
        if (step?.spec && typeof step.toolCallId === "string") found.push(step);
        for (const nested of Array.isArray(step?.results) ? step.results : []) found.push(...harnessQuerySteps(nested, depth + 1));
    }
    for (const nested of Array.isArray(details.results) ? details.results : []) found.push(...harnessQuerySteps(nested, depth + 1));
    return found;
}

function indent(text, leader) {
    if (!text) return "";
    const lines = text.split("\n");
    return leader + lines[0] + (lines.length > 1 ? "\n" + lines.slice(1).map((l) => " ".repeat(leader.length) + l).join("\n") : "");
}

function safeJson(value) {
    try { return JSON.stringify(value); } catch { return String(value); }
}

function describeBriefData(data) {
    if (Array.isArray(data)) {
        if (!data.length) return "[] (empty array)";
        if (typeof data[0] === "number") return `[number…] · ${data.length} items`;
        if (typeof data[0] === "object") return `[row…] · ${data.length} rows · sample keys=[${Object.keys(data[0]).slice(0, 6).join(", ")}]`;
        return `[…] · ${data.length} items`;
    }
    if (data && typeof data === "object") return `object · keys=[${Object.keys(data).slice(0, 6).join(", ")}]`;
    return String(data);
}

function truncate(text, max) {
    if (text == null) return "";
    const s = String(text);
    return s.length > max ? `${s.slice(0, max)}…` : s;
}

// ── sub-agent slot helpers (mirrors subAgentTools.forwardChildEvent) ──

function makeSlot() {
    return {
        status: "running",
        activity: "starting…",
        tools: [],
        preview: "",
        messages: [],
    };
}

const PREVIEW_TAIL = 200;

function forwardChildEvent(slot, event) {
    if (!slot) return;
    refreshMirror(slot);
    switch (event.type) {
        case "turn_start":
            slot.activity = "thinking…";
            slot.preview = "";
            return;
        case "tool_execution_start": {
            const name = event.toolName || "tool";
            slot.tools = [...(slot.tools || []), { name, status: "running", summary: "" }];
            slot.activity = `running ${name}…`;
            return;
        }
        case "tool_execution_end": {
            const tools = (slot.tools || []).slice();
            const last = tools.length ? tools[tools.length - 1] : null;
            const details = event.result?.details;
            const summary = summariseToolResult(details, last?.name) || (event.isError ? "error" : "done");
            if (last) {
                tools[tools.length - 1] = {
                    ...last,
                    status: event.isError ? "error" : "done",
                    summary,
                };
            }
            slot.tools = tools.length > 6 ? tools.slice(-6) : tools;
            slot.activity = event.isError ? "tool failed" : "thinking…";
            return;
        }
        case "message_update": {
            const inner = event.assistantMessageEvent;
            if (!inner) return;
            if (inner.type === "text_delta" && inner.delta) {
                slot.preview = ((slot.preview || "") + inner.delta).slice(-PREVIEW_TAIL);
                slot.activity = "writing…";
            } else if (inner.type === "thinking_delta" && inner.delta) {
                slot.preview = ((slot.preview || "") + inner.delta).slice(-PREVIEW_TAIL);
                slot.activity = "reasoning…";
            } else if (inner.type === "toolcall_start") {
                slot.activity = "preparing tool call…";
            }
            return;
        }
        default:
            return;
    }
}

function summariseToolResult(details, toolName) {
    if (!details) return "";
    if (toolName === "test_kernel") {
        if (details.ok === true) return details.summary || "ok";
        if (details.ok === false) return details.summary || details.error || "failed";
    }
    if (toolName === "return_result") return "returned";
    if (toolName?.startsWith("schema_")) {
        if (Array.isArray(details.entities)) return `${details.entities.length} entities`;
        if (Array.isArray(details.matches)) return `${details.matches.length} matches`;
        if (details.name) return details.name;
    }
    return "";
}
